/// <amd-module name="Feed/Legacy/Protocols/Maven/MavenPackageProtocol" />
import * as React from "react";

import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";

import { FeedRole, FeedView, Package, PackageMetrics, PackageVersion, PackageVersionMetrics } from "Feed/Common/Contracts/Feed";
import * as MavenContracts from "Feed/Common/Protocols/Maven/Contracts/Maven";
import { UpstreamingBehavior } from "Feed/Common/Protocols/Shared/Contracts/PackagingShared";
import * as PackageResources from "Feed/Common/Resources";
import { BowtieIconProps } from "Feed/Common/Utils/Icons";
import { ProtocolCommands } from "Feed/Legacy/Common/ProtocolCommands";
import { HubWebPageDataService } from "Feed/Legacy/DataServices/WebPageDataService";
import { isV2Feed } from "Feed/Legacy/Helpers/FeedCapabilityHelper";
import * as PermissionHelper from "Feed/Legacy/Helpers/PermissionHelper";
import { IBatchIterator, TransformingBatchGenerator } from "Feed/Legacy/Protocols/Common/BatchHandling";
import { DeleteCommandHelper } from "Feed/Legacy/Protocols/Common/DeleteCommandHelper";
import { IPackageDetailsProps } from "Feed/Legacy/Protocols/Common/IPackageDetailsProps";
import { IPackageProtocol, IPackageProtocolClient, isPackage } from "Feed/Legacy/Protocols/Common/IPackageProtocol";
import { ISupportedFeatures } from "Feed/Legacy/Protocols/Common/ISupportedFeatures";
import { MinimalPackageDetails } from "Feed/Legacy/Protocols/Common/WebApi/PackagingShared.Contracts";
import { MavenKey } from "Feed/Legacy/Protocols/Maven/Constants/MavenConstants";
import { GradleClientTool } from "Feed/Legacy/Protocols/Maven/GradleClientTool";
import { MavenClientTool } from "Feed/Legacy/Protocols/Maven/MavenClientTool";
import { IFeedMavenDataService } from "Feed/Legacy/Protocols/Maven/MavenDataService";
import * as MavenInstallTextHelper from "Feed/Legacy/Protocols/Maven/MavenInstallTextHelper";
import { MavenOverviewAttributesPanel } from "Feed/Legacy/Protocols/Maven/MavenOverviewAttributesPanel";
import { IMavenPackageDetailsProps, MavenOverviewContentPanel } from "Feed/Legacy/Protocols/Maven/MavenOverviewContentPanel";
import { IPackageCommand } from "Feed/Legacy/Types/IPackageCommand";
import { Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

import { PromotePackageCommand, PromotePackagesCommand } from "../Common/PackageCommands";

export class MavenPackageProtocol implements IPackageProtocol {
    private static readonly _icon = new BowtieIconProps("brand-maven");

    public readonly name = "Maven";
    public readonly key = MavenKey;
    get vssIconProps() {
        return MavenPackageProtocol._icon;
    }
    public get supportedCommandsMask(): ProtocolCommands {
        // tslint:disable-next-line:no-bitwise
        return ProtocolCommands.Delete | ProtocolCommands.Promote;
    }
    public readonly clients: IPackageProtocolClient[] = [];

    private pageContext: IVssPageContext;

    constructor(pageContext: IVssPageContext) {
        this.pageContext = pageContext;
        this.clients.push(new MavenClientTool(this.pageContext), new GradleClientTool(this.pageContext));
    }

    get MavenDataService(): IFeedMavenDataService {
        return this.pageContext.getService<IFeedMavenDataService>("IFeedMavenDataService");
    }

    get WebPageDataService(): HubWebPageDataService {
        return this.pageContext.getService<HubWebPageDataService>("IFeedWebPageDataService");
    }

    get batchSize(): number {
        return this.MavenDataService.batchSize;
    }

    public getPackageMessage(packageSummary: Package, packageVersion: PackageVersion): string {
        if (packageVersion && packageVersion.isDeleted) {
            return Utils_String.format(
                PackageResources.PackageDeletedMessage,
                packageVersion.version,
                packageSummary.name
            );
        }
    }

    public getOverviewAttributesPanel(
        feed: Feed,
        packageSummary: Package,
        packageVersion: PackageVersion,
        packageVersionMetrics: PackageVersionMetrics,
        packageMetrics: PackageMetrics,
        isSmartDependenciesEnabled: boolean,
        isProvenanceEnabled: boolean,
        isMetricsEnabled: boolean,
        isMetricsLoading: boolean
    ): React.ReactNode {
        return React.createElement(MavenOverviewAttributesPanel, {
            pageContext: this.pageContext,
            feed,
            packageSummary,
            packageVersion,
            protocol: this,
            packageVersionMetrics,
            packageMetrics,
            isSmartDependenciesEnabled,
            isProvenanceEnabled,
            isMetricsEnabled,
            isMetricsLoading
        } as IPackageDetailsProps);
    }

    public getOverviewContentPanel(
        feed: Feed,
        feedViews: FeedView[],
        packageSummary: Package,
        packageVersion: PackageVersion
    ): React.ReactNode {
        return React.createElement(MavenOverviewContentPanel, {
            pageContext: this.pageContext,
            feed,
            feedViews,
            packageSummary,
            packageVersion,
            protocol: this,
            minimumSnapshotInstanceCount: this.getMinimumSnapshotInstanceCount()
        } as IMavenPackageDetailsProps);
    }

    public getMultiSelectPackageCommands(
        feed: Feed,
        selectedPackages?: Package[],
        selectedVersions?: PackageVersion[]
    ): IPackageCommand[] {
        const packageCommands = new Array<IPackageCommand>();

        const role: FeedRole = PermissionHelper.getUsersRoleForFromFeed(feed);
        const showDisabledCommands = role === FeedRole.None || role === FeedRole.Reader;

        // Promote (only works when multiple packages are selected, not multiple versions)
        if (selectedPackages && selectedPackages.length > 0) {
            packageCommands.push(new PromotePackagesCommand(selectedPackages, showDisabledCommands));
        }

        const deleteCommands = DeleteCommandHelper.addDeleteCommands(
            MavenKey,
            selectedPackages,
            selectedVersions,
            null /*packageName*/,
            !PermissionHelper.isAdministratorFromRole(role) /*disabled*/
        );
        packageCommands.push(...deleteCommands);

        return packageCommands;
    }

    // get a list of available package commands for this protocol based on the user's role
    public getPackageCommands(
        packageSummary: Package,
        packageVersion: PackageVersion,
        feed: Feed,
        viaPackageList: boolean = false
    ): IPackageCommand[] {
        const packageCommands: IPackageCommand[] = [];

        if (packageVersion.isDeleted) {
            return packageCommands;
        }

        const role: FeedRole = PermissionHelper.getUsersRoleForFromFeed(feed);
        packageCommands.push(
            new PromotePackageCommand(
                packageSummary,
                viaPackageList,
                !(PermissionHelper.isContributor(role) || PermissionHelper.isAdministratorFromRole(role))
            )
        );

        const deleteCommands = DeleteCommandHelper.addDeleteCommands(
            MavenKey,
            [packageSummary], // Always include packageSummary to be able to read protocolType
            viaPackageList ? null : [packageVersion],
            packageSummary.name,
            !PermissionHelper.isAdministratorFromRole(role) /*disabled*/
        );
        packageCommands.push(...deleteCommands);

        return packageCommands;
    }

    public supportedFeatures(feed?: Feed): ISupportedFeatures {
        const validCapability = feed ? isV2Feed(feed) : true;
        const features: ISupportedFeatures = {
            upstreams: validCapability,
            projectScopedFeeds: true,
            publicUpstreams: true
        };
        return features;
    }

    // Use supportedFeatures instead.
    // Except for connect to feed dialog that is located in the old Feed/Service/Web location.
    public supportsUpstreams(feed?: Feed): boolean {
        return feed ? isV2Feed(feed) : true;
    }

    public async deleteLatestVersions(projectId: string, feed: Feed, pkgs: Package[]): Promise<void> {
        try {
            const iterator = new TransformingBatchGenerator(pkgs, this.MavenDataService.batchSize, (pkg: Package) => {
                const pomGav = this.toPomGav({ id: pkg.name, version: pkg.versions[0].normalizedVersion });
                return {
                    group: pomGav.groupId,
                    artifact: pomGav.artifactId,
                    version: pomGav.version
                };
            });
            await this.MavenDataService.batchDeletePackageVersions(feed, iterator);
        } catch (error) {
            throw new Error(this.key);
        }
    }

    public async deletePackageVersions(
        projectId: string,
        feed: Feed,
        pkg: Package,
        versions: PackageVersion[]
    ): Promise<void> {
        try {
            const iterator = new TransformingBatchGenerator(versions, this.MavenDataService.batchSize, pkgVersion =>
                this.fromPackageVersion(pkg.name, pkgVersion)
            );

            await this.MavenDataService.batchDeletePackageVersions(feed, iterator);
        } catch (error) {
            throw new Error(this.key);
        }
    }

    public async restorePackageVersionsToFeed(
        projectId: string,
        feed: Feed,
        pkg: Package,
        versions: PackageVersion[]
    ): Promise<void> {
        const iterator = new TransformingBatchGenerator(versions, this.batchSize, pkgVersion =>
            this.fromPackageVersion(pkg.name, pkgVersion)
        );
        await this.MavenDataService.batchRestoreToFeed(feed, iterator);
    }

    public permanentlyDeletePackageVersions(
        projectId: string,
        feed: Feed,
        pkg: Package,
        versions: PackageVersion[]
    ): Promise<void>;
    public permanentlyDeletePackageVersions(
        projectId: string,
        feed: Feed,
        packageVersions: MinimalPackageDetails[]
    ): Promise<void>;
    public async permanentlyDeletePackageVersions(
        projectId: string,
        feed: Feed,
        pkgOrDetails: Package | MinimalPackageDetails[],
        versions?: PackageVersion[]
    ) {
        let iterator: IBatchIterator<MavenContracts.MavenMinimalPackageDetails[]>;

        if (isPackage(pkgOrDetails)) {
            const pkg = pkgOrDetails;
            iterator = new TransformingBatchGenerator(versions, this.batchSize, pkgVersion =>
                this.fromPackageVersion(pkg.name, pkgVersion)
            );
        } else {
            iterator = new TransformingBatchGenerator(pkgOrDetails, this.batchSize, minPkgDetails =>
                this.fromMinPackageDetails(minPkgDetails)
            );
        }

        await this.MavenDataService.batchPermanentlyDelete(feed, iterator);
    }

    public promotePackageVersions(
        projectId: string,
        feed: Feed,
        view: FeedView,
        pkg: Package,
        versions: PackageVersion[]
    ): Promise<void>;
    public promotePackageVersions(
        projectId: string,
        feed: Feed,
        view: FeedView,
        packageVersions: MinimalPackageDetails[]
    ): Promise<void>;
    public async promotePackageVersions(
        projectId: string,
        feed: Feed,
        view: FeedView,
        pkgOrDetails: Package | MinimalPackageDetails[],
        versions?: PackageVersion[]
    ): Promise<void> {
        let iterator: IBatchIterator<MavenContracts.MavenMinimalPackageDetails[]>;

        if (isPackage(pkgOrDetails)) {
            const pkg = pkgOrDetails;
            iterator = new TransformingBatchGenerator(versions, this.batchSize, pkgVersion =>
                this.fromPackageVersion(pkg.name, pkgVersion)
            );
        } else {
            iterator = new TransformingBatchGenerator(pkgOrDetails, this.batchSize, minPkgDetails =>
                this.fromMinPackageDetails(minPkgDetails)
            );
        }

        await this.MavenDataService.batchPromotePackages(feed, view.id, iterator);
    }

    public getCopyInstallCommand(feed: Feed, pkgName: string, pkgVersion: PackageVersion, separator?: string): string {
        return MavenInstallTextHelper.getMavenPackageInstallText(pkgVersion.version, pkgName);
    }

    public getUpstreamingBehavior(feed: Feed, pkg: Package): Promise<UpstreamingBehavior> {
        return this.MavenDataService.getUpstreamingBehavior(feed, pkg);
    }

    public setUpstreamingBehavior(feed: Feed, pkg: Package, behavior: UpstreamingBehavior): Promise<void> {
        return this.MavenDataService.setUpstreamingBehavior(feed, pkg, behavior);
    }

    private getMinimumSnapshotInstanceCount(): number {
        return this.WebPageDataService.minimumSnapshotInstanceCount();
    }

    private toPomGav(packageDetails: MinimalPackageDetails): MavenContracts.MavenPomGav {
        // Maven packages are of the form groupId:artifactId. Anything other than that are rejected.
        const nameParts = (packageDetails.id || "").split(":");
        if (nameParts.length !== 2) {
            throw new Error(PackageResources.MavenOverview_InvalidPackageError);
        }

        return <MavenContracts.MavenPomGav>{
            groupId: nameParts[0],
            artifactId: nameParts[1],
            version: packageDetails.version
        };
    }

    private fromMinPackageDetails(packageDetails: MinimalPackageDetails): MavenContracts.MavenMinimalPackageDetails {
        const { groupId, artifactId, version } = this.toPomGav(packageDetails);

        return <MavenContracts.MavenMinimalPackageDetails>{
            group: groupId,
            artifact: artifactId,
            version
        };
    }

    private fromPackageVersion(name: string, pkgVersion: PackageVersion): MavenContracts.MavenMinimalPackageDetails {
        const minimalPackageDetails: MinimalPackageDetails = <MinimalPackageDetails>{
            id: name,
            version: pkgVersion.normalizedVersion
        };
        const { groupId, artifactId, version } = this.toPomGav(minimalPackageDetails);

        return <MavenContracts.MavenMinimalPackageDetails>{
            group: groupId,
            artifact: artifactId,
            version
        };
    }
}
